import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cloneElement, useId } from "react";
import { Label } from "../atoms/label";
import { cn } from "../../lib/cn";
/**
 * Label, control, hint and error, wired together.
 *
 * The wiring is the entire point. Doing it by hand means four ids per field
 * and it is done wrong more often than right: the hint is left unassociated,
 * the error is announced only visually, or `aria-invalid` never appears. Here
 * the control is cloned with the ids already attached.
 *
 * The error carries `role="alert"`, so it is announced when it appears rather
 * than only when focus reaches the field. The hint is not: it is on the page
 * before anything happens and `aria-describedby` already reaches it.
 */
export function FormField({ label, children, hint, error, required = false, className, id, }) {
    const generated = useId();
    const controlId = id ?? `field-${generated}`;
    const hintId = `${controlId}-hint`;
    const errorId = `${controlId}-error`;
    const describedBy = [error !== undefined ? errorId : null, hint !== undefined ? hintId : null]
        .filter((value) => value !== null)
        .join(" ");
    // Built by assignment rather than as one literal, because
    // `exactOptionalPropertyTypes` means an explicit `undefined` is not the same
    // as an absent key, and cloneElement would otherwise overwrite whatever the
    // caller set with nothing.
    const overrides = {};
    if (children.props.id === undefined)
        overrides.id = controlId;
    if (children.props["aria-describedby"] === undefined && describedBy !== "") {
        overrides["aria-describedby"] = describedBy;
    }
    if (children.props["aria-invalid"] === undefined && error !== undefined) {
        overrides["aria-invalid"] = true;
    }
    const control = cloneElement(children, overrides);
    return (_jsxs("div", { "data-slot": "form-field", className: cn("lg:flex lg:flex-col lg:gap-2", className), children: [_jsxs(Label, { htmlFor: controlId, children: [label, required && (_jsx("span", { className: "lg:ml-1 lg:text-primary", "aria-hidden": "true", children: "*" })), required && _jsx("span", { className: "lg:sr-only", children: " (required)" })] }), control, hint !== undefined && error === undefined && (_jsx("p", { id: hintId, className: "lg:text-caption lg:text-subtle lg:max-w-none", children: hint })), error !== undefined && (_jsx("p", { id: errorId, role: "alert", className: "lg:text-caption lg:text-destructive lg:max-w-none", children: error }))] }));
}
//# sourceMappingURL=form-field.js.map